import VerticalTabs from '@/components/VerticalTabs';
import languageRuleSlice, { selectedLanguageRuleSelector } from '@/redux/slice/languageRule.slice';
import { rulesSelector } from '@/redux/slice/rules.slice';
import { Button, Card, Icon, Tabs, TextField } from '@shopify/polaris';
import { CartIcon, CheckIcon, DeliveryIcon, LanguageTranslateIcon, SearchIcon, WandIcon } from '@shopify/polaris-icons';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ModalContentStyled } from './styled';
import LanguageItem from '../LanguageItem';
import EditContent from '../EditContent';
import EditAppearance from '../EditAppearance';
import EditETACheckout from '../EditETACheckout';
import useTranslate from '@/hooks/useTranslate';
import { ILanguageRuleContent } from '@/types/languageRule.type';
import BaseModal from '@/components/BaseModal';

const typeTabs = [
  {
    id: 'rule',
    label: 'Estimate rule content',
    prefixIcon: DeliveryIcon,
    suffixIcon: CheckIcon,
  },
  {
    id: 'appearanceContent',
    label: 'Appearance content',
    prefixIcon: WandIcon,
    suffixIcon: CheckIcon,
  },
  {
    id: 'ETACheckoutExtension',
    label: 'ETA Checkout extension',
    prefixIcon: CartIcon,
    suffixIcon: CheckIcon,
  },
];

const filterTabs = [
  {
    id: 'all-rules',
    content: 'All',
    accessibilityLabel: 'All rules',
    panelID: 'all-rules',
  },
  {
    id: 'added-rules',
    content: 'Added',
    panelID: 'added-rules',
  },
  {
    id: 'not-added-rules',
    content: 'Not added',
    panelID: 'not-added-rules',
  },
];

const ModalContent = () => {
  const dispatch = useDispatch();
  const currentLanguageRule = useSelector(selectedLanguageRuleSelector);
  const { allRules } = useSelector(rulesSelector);
  const { translateText, isShowTranslate } = useTranslate();

  const [filterTabSelected, setFilterTabSelected] = useState(0);
  const [tabTypeSelected, setTabTypeSelected] = useState('rule');
  const [searchValue, setSearchValue] = useState('');
  const [ruleSelected, setRuleSelected] = useState<number | undefined>();
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 20 });
  const [isLoadingAutoTranslate, setIsLoadingAutoTranslate] = useState(false);
  const [isShowConfirmModal, setIsShowConfirmModal] = useState(false);
  const [pendingAutoTranslate, setPendingAutoTranslate] = useState<null | (() => void)>(null);

  const ITEM_HEIGHT = 68;
  const containerRef = useRef<HTMLDivElement>(null);

  const handleFilterTabChange = (selectedTabIndex: number) => {
    setFilterTabSelected(selectedTabIndex);
  };

  const listRuleAddedId = useMemo(() => {
    return currentLanguageRule.ruleContents.map((rule) => rule.id);
  }, [currentLanguageRule.ruleContents]);

  const currentRules = useMemo(() => {
    let temp = allRules.map((rule) => {
      if (rule.id && listRuleAddedId.includes(rule.id)) {
        return { ...rule, isAdded: true };
      }
      return { ...rule, isAdded: false };
    });

    switch (filterTabSelected) {
      case 1:
        return temp.filter((rule) => rule.isAdded);
      case 2:
        return temp.filter((rule) => !rule.isAdded);
      default:
        break;
    }
    if (searchValue) {
      return temp.filter((rule) => rule.name.toLowerCase().includes(searchValue.toLowerCase()));
    }
    return temp;
  }, [allRules, filterTabSelected, listRuleAddedId, searchValue]);

  const isCurrentRuleAdded = useMemo(() => {
    const allAddedRules = allRules.map((rule) => {
      if (rule.id && listRuleAddedId.includes(rule.id)) {
        return { ...rule, isAdded: true };
      }
      return { ...rule, isAdded: false };
    });
    return allAddedRules.find((rule) => rule.id === ruleSelected)?.isAdded;
  }, [allRules, listRuleAddedId, ruleSelected]);

  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;

    const scrollTop = containerRef.current.scrollTop;
    const start = Math.floor(scrollTop / ITEM_HEIGHT);
    const visibleItems = Math.ceil(containerRef.current.clientHeight / ITEM_HEIGHT);

    const bufferItems = 5;
    setVisibleRange({
      start: Math.max(0, start - bufferItems),
      end: Math.min(currentRules.length, start + visibleItems + bufferItems),
    });
  }, [currentRules.length]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      handleScroll();
    }
    return () => container?.removeEventListener('scroll', handleScroll);
  }, [currentRules.length, handleScroll]);

  const handleAddRule = (id: number) => {
    dispatch(languageRuleSlice.actions.handleAddRuleContent(id));
    setRuleSelected(id);
  };

  const handleRemoveRule = (id: number) => {
    dispatch(languageRuleSlice.actions.handleRemoveRuleContent(id));
    if (ruleSelected === id) {
      setRuleSelected(undefined);
    }
  };

  const handleAutoTranslateAllRules = async () => {
    setIsShowConfirmModal(false);
    setIsLoadingAutoTranslate(true);
    try {
      // Lấy danh sách rule theo tab đang chọn
      let rulesToProcess: typeof allRules = [];
      if (filterTabSelected === 0) {
        rulesToProcess = allRules;
      } else if (filterTabSelected === 1) {
        rulesToProcess = allRules.filter((rule) => typeof rule.id === 'number' && listRuleAddedId.includes(rule.id));
      } else if (filterTabSelected === 2) {
        rulesToProcess = allRules.filter((rule) => typeof rule.id === 'number' && !listRuleAddedId.includes(rule.id));
      }
      console.log('rulesToProcess:', rulesToProcess);
      // Tạo danh sách ruleContents mới:
      let newRuleContents: ILanguageRuleContent[] = [];
      if (filterTabSelected === 2) {
        // Nếu là tab Not added: giữ nguyên rule đã add, chỉ add và dịch các rule chưa add
        const addedIds = currentLanguageRule.ruleContents.map((r) => r.id).filter((id) => typeof id === 'number');
        const rulesToAdd = rulesToProcess.filter((rule) => typeof rule.id === 'number' && !addedIds.includes(rule.id));
        newRuleContents = [
          ...currentLanguageRule.ruleContents,
          ...rulesToAdd.map((rule) => ({
            id: rule.id || -1,
            name: '',
            estimatedText: '',
            privacyPageText: '',
            cartPageDateDisplay: '',
            privacyMore: '',
            soldOutCustomText: '',
            cartPageLabelText: '',
          })),
        ];
      } else {
        // Các tab khác: chỉ chứa các rule thuộc rulesToProcess, mỗi rule là bản dịch mới nhất
        for (const rule of rulesToProcess) {
          newRuleContents.push({
            id: rule.id || -1,
            name: '',
            estimatedText: '',
            privacyPageText: '',
            cartPageDateDisplay: '',
            privacyMore: '',
            soldOutCustomText: '',
            cartPageLabelText: '',
          });
        }
      }
      // Dịch tất cả rule trong newRuleContents
      const translatedRuleContents = await Promise.all(
        newRuleContents.map(async (ruleContent) => {
          // Tìm rule gốc
          const ruleOrigin = allRules.find((r) => r.id === ruleContent.id);
          if (!ruleOrigin) return ruleContent;
          // Nếu là tab Not added, chỉ dịch các rule vừa add, giữ nguyên rule đã add
          if (filterTabSelected === 2 && currentLanguageRule.ruleContents.find((r) => r.id === ruleContent.id)) {
            return ruleContent;
          }
          // Dịch các trường
          const isHasTooltip = !!ruleOrigin.privacyPageText || !!ruleOrigin.privacyMore;
          const isShowTextInCart = ruleOrigin.showTextInCart;
          const isShowForSoldOut = ruleOrigin.soldOutEnabled;
          const name = await translateText(ruleOrigin.name);
          const estimatedText = await translateText(ruleOrigin.estimatedText || '');
          const privacyPageText = isHasTooltip ? await translateText(ruleOrigin.privacyPageText || '') : '';
          const privacyMore = isHasTooltip ? await translateText(ruleOrigin.privacyMore || '') : '';
          const cartPageLabelText = isShowTextInCart ? await translateText(ruleOrigin.cartPageLabelText || '') : '';
          const cartPageDateDisplay = isShowTextInCart ? await translateText(ruleOrigin.cartPageDateDisplay || '') : '';
          const soldOutCustomText = isShowForSoldOut ? await translateText(ruleOrigin.soldOutCustomText || '') : '';
          return {
            id: ruleContent.id,
            name,
            estimatedText,
            privacyPageText,
            privacyMore,
            cartPageLabelText,
            cartPageDateDisplay,
            soldOutCustomText,
          };
        }),
      );
      // Dispatch cập nhật state
      dispatch(
        languageRuleSlice.actions.handleSetSelectedRule({
          ...currentLanguageRule,
          ruleContents: translatedRuleContents,
        }),
      );
      // Set lại selected rule là rule đầu tiên trong danh sách đã dịch (nếu có)
      if (translatedRuleContents.length > 0) {
        setRuleSelected(translatedRuleContents[0].id);
      }
    } catch (e) {
      // Có thể show toast lỗi nếu muốn
    }
    setIsLoadingAutoTranslate(false);
  };

  const handleClickAutoTranslate = () => {
    setIsShowConfirmModal(true);
    setPendingAutoTranslate(() => handleAutoTranslateAllRules);
  };

  return (
    <ModalContentStyled id="modal-cu-language-rule">
      <div className="d-flex flex-column gap-8">
        <Card padding="0">
          <VerticalTabs tabs={typeTabs} selected={tabTypeSelected} onChange={setTabTypeSelected} />
        </Card>

        {tabTypeSelected === 'rule' ? (
          <>
            <div className="mt-8">
              <TextField
                labelHidden
                label="Search"
                autoComplete="off"
                value={searchValue}
                placeholder="Search rule name"
                prefix={<Icon source={SearchIcon} />}
                clearButton
                onClearButtonClick={() => {
                  setSearchValue('');
                }}
                onChange={(value) => {
                  setSearchValue(value);
                }}
              />
            </div>

            <div className="filter-tabs-container">
              <Tabs tabs={filterTabs} selected={filterTabSelected} onSelect={handleFilterTabChange} fitted>
                {currentRules.length > 0 ? (
                  <div className="mt-8 mb-8">
                    <Button
                      icon={LanguageTranslateIcon}
                      onClick={handleClickAutoTranslate}
                      loading={isLoadingAutoTranslate}
                      disabled={isLoadingAutoTranslate || !isShowTranslate}
                    >
                      Auto translate {filterTabs[filterTabSelected].content.toLowerCase()} rules
                    </Button>
                  </div>
                ) : null}
                <Card padding="0">
                  <div
                    ref={containerRef}
                    style={{
                      height: '50vh',
                      overflowY: 'auto',
                      position: 'relative',
                    }}
                  >
                    <div style={{ height: `${currentRules.length * ITEM_HEIGHT}px` }}>
                      {currentRules.slice(visibleRange.start, visibleRange.end).map((item, index) => {
                        const actualIndex = index + visibleRange.start;
                        const estDeliveryDate = `${item.minPrepDays}-${item.maxDeliveryDays}-${item.minDeliveryDays}-${item.maxDeliveryDays}`;

                        return (
                          <div
                            key={item.id}
                            style={{
                              position: 'absolute',
                              top: `${actualIndex * ITEM_HEIGHT}px`,
                              width: '100%',
                            }}
                          >
                            <LanguageItem
                              onSelect={() => setRuleSelected(item.id)}
                              onAction={() => {
                                if (typeof item.id === 'number') {
                                  if (item.isAdded) {
                                    handleRemoveRule(item.id);
                                  } else {
                                    handleAddRule(item.id);
                                  }
                                }
                              }}
                              name={item.name}
                              estDeliveryDate={estDeliveryDate}
                              isAdded={item.isAdded}
                              isSelected={item.id === ruleSelected}
                              isHasBottomBorder={actualIndex !== currentRules.length - 1}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </Card>
              </Tabs>
            </div>
          </>
        ) : null}
      </div>
      {tabTypeSelected === 'rule' ? (
        <EditContent isAdded={isCurrentRuleAdded} ruleSelectedId={ruleSelected || 0} />
      ) : tabTypeSelected === 'appearanceContent' ? (
        <EditAppearance />
      ) : (
        <EditETACheckout />
      )}
      {/* Modal confirm auto translate */}
      <BaseModal
        open={isShowConfirmModal}
        title={`Auto translate ${filterTabs[filterTabSelected].content.toLowerCase()} rules?`}
        onClose={() => setIsShowConfirmModal(false)}
        primaryAction={{
          content: 'Confirm',
          onAction: () => {
            setIsShowConfirmModal(false);
            if (pendingAutoTranslate) pendingAutoTranslate();
          },
          loading: isLoadingAutoTranslate,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setIsShowConfirmModal(false),
          },
        ]}
      >
        <div id="confirm-auto-translate-content">
          Are you sure you want to automatically translate and replace the content for{' '}
          {filterTabs[filterTabSelected].content.toLowerCase()} rules? The old content will be overwritten.
        </div>
      </BaseModal>
    </ModalContentStyled>
  );
};

export default memo(ModalContent);
